import React, { useCallback, useEffect, useState } from 'react';
import RialSymbol from '../../components/RialSymbol';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  LocalShipping as DeliveryIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  TrendingUp as EarningsIcon,
} from '@mui/icons-material';
import LoadingAnimation from '../../components/LoadingAnimation';
import DeliveryStatusWidget from '../../components/DeliveryStatusWidget';
import deliveryService from '../../services/delivery.service';

interface DeliveryStats {
  pendingRequests: number;
  completedToday: number;
  weekDeliveries: number;
  todayEarnings: number;
}

const formatOMR = (value: number) => <><RialSymbol /> {Number(value || 0).toFixed(2)}</>;

const formatTime = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Best-effort human label for a delivery's order, tolerant of populated docs,
// bare ObjectIds, or missing data.
const orderLabel = (delivery: any) => {
  const order = delivery?.order;
  if (order?.orderNumber) return `#${order.orderNumber}`;
  const id = order?._id || order || delivery?._id;
  return id ? `#${String(id).slice(-6).toUpperCase()}` : '#—';
};

export default function DeliveryDashboard() {
  const [stats, setStats] = useState<DeliveryStats | null>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // All data here comes from endpoints the backend scopes to the authenticated
  // delivery person, so each driver only ever sees and acts on their own jobs.
  const loadData = useCallback(async () => {
    let pending: any[] = [];
    let summary: any = null;

    try {
      const res = await deliveryService.getPending();
      pending = res?.data || [];
    } catch (err) {
      pending = [];
    }

    try {
      const res = await deliveryService.getEarningsSummary();
      summary = res?.data || null;
    } catch (err) {
      summary = null;
    }

    setRequests(pending);
    setStats({
      pendingRequests: pending.length,
      completedToday: summary?.today?.deliveries || 0,
      weekDeliveries: summary?.week?.deliveries || 0,
      todayEarnings: summary?.today?.earnings || 0,
    });
  }, []);

  useEffect(() => {
    (async () => {
      await loadData();
      setLoading(false);
    })();
  }, [loadData]);

  const handleAccept = async (delivery: any) => {
    setActingId(delivery._id);
    try {
      await deliveryService.acceptDelivery(delivery._id);
      setToast({ msg: `Delivery ${orderLabel(delivery)} accepted`, severity: 'success' });
      await loadData();
    } catch (err: any) {
      setToast({
        msg: err?.response?.data?.message || 'Could not accept this delivery',
        severity: 'error',
      });
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (delivery: any) => {
    setActingId(delivery._id);
    try {
      await deliveryService.rejectDelivery(delivery._id);
      setToast({ msg: `Delivery ${orderLabel(delivery)} rejected`, severity: 'success' });
      await loadData();
    } catch (err: any) {
      setToast({
        msg: err?.response?.data?.message || 'Could not reject this delivery',
        severity: 'error',
      });
    } finally {
      setActingId(null);
    }
  };

  if (loading) {
    return <LoadingAnimation message="Loading delivery dashboard..." subtitle="Ready for deliveries" />;
  }

  const statCards = [
    {
      label: 'Delivery Requests',
      value: stats?.pendingRequests ?? 0,
      icon: <PendingIcon sx={{ color: 'white', fontSize: 28 }} />,
      bg: 'warning.main',
    },
    {
      label: 'Completed Today',
      value: stats?.completedToday ?? 0,
      icon: <CompletedIcon sx={{ color: 'white', fontSize: 28 }} />,
      bg: 'success.main',
    },
    {
      label: 'This Week',
      value: stats?.weekDeliveries ?? 0,
      icon: <DeliveryIcon sx={{ color: 'white', fontSize: 28 }} />,
      bg: '#0B6E4F',
    },
    {
      label: "Today's Earnings",
      value: formatOMR(stats?.todayEarnings ?? 0),
      icon: <EarningsIcon sx={{ color: 'white', fontSize: 28 }} />,
      bg: '#FF6B35',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Hello, Driver! 🚚
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your deliveries and track your earnings
        </Typography>
      </Box>

      {/* Delivery Status Widget */}
      <Box sx={{ mb: 4 }}>
        <DeliveryStatusWidget />
      </Box>

      {/* Stats Cards — scoped to the logged-in driver */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        {statCards.map((card) => (
          <Card key={card.label}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: card.bg, borderRadius: 2, p: 1.5, mr: 2, display: 'flex' }}>
                  {card.icon}
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.label}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Delivery Requests — the live queue of jobs waiting for THIS driver.
          Backend: GET /api/delivery/pending (unassigned "pending" + jobs
          assigned to this delivery person). */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Delivery Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            New requests and jobs assigned to you. Accept one to add it to your active deliveries.
          </Typography>

          {requests.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No delivery requests right now. Set your status to “Available” to receive new jobs.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {requests.map((delivery) => {
                const time = formatTime(delivery?.scheduledPickupTime);
                const location =
                  delivery?.deliveryAddress?.city ||
                  delivery?.deliveryAddress?.fullAddress ||
                  'Address on file';
                const isAssigned = delivery?.status === 'assigned';
                const busy = actingId === delivery._id;
                return (
                  <ListItem
                    key={delivery._id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      mb: 2,
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 600 }}>Order {orderLabel(delivery)}</Typography>
                          <Chip
                            label={isAssigned ? 'Assigned to you' : 'New request'}
                            color={isAssigned ? 'primary' : 'success'}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          📍 {location}
                          {time ? ` • ⏰ ${time}` : ''}
                        </>
                      }
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={busy}
                        onClick={() => handleAccept(delivery)}
                      >
                        {busy ? 'Working…' : 'Accept'}
                      </Button>
                      {isAssigned && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => handleReject(delivery)}
                        >
                          Reject
                        </Button>
                      )}
                    </Stack>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
